import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Loader2, Mail, Phone, Building2, Trash2, Download, Search, ChevronDown, ChevronUp } from 'lucide-react';
import {
  fetchAdminQuotes, updateQuoteStatus, deleteQuote, exportQuotesCsvUrl,
} from '@/lib/api';
import { ADMIN_QUOTES } from '@/constants/testIds';
import { toast } from 'sonner';

const STATUSES = [
  { slug: 'all',       label: 'All' },
  { slug: 'new',       label: 'New' },
  { slug: 'contacted', label: 'Contacted' },
  { slug: 'quoted',    label: 'Quoted' },
  { slug: 'won',       label: 'Won' },
  { slug: 'lost',      label: 'Lost' },
  { slug: 'closed',    label: 'Closed' },
];

const STATUS_COLOR = {
  new:       'border-[var(--mw-primary)]/60 text-[var(--mw-primary)]',
  contacted: 'border-amber-400/60 text-amber-300',
  quoted:    'border-sky-400/60 text-sky-300',
  won:       'border-emerald-400/60 text-emerald-300',
  lost:      'border-rose-500/60 text-rose-400',
  closed:    'border-white/20 text-white/55',
};

export default function QuotesPanel() {
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  const { data, isLoading, mutate } = useSWR(
    ['admin-quotes', status],
    ([, s]) => fetchAdminQuotes(s),
    { keepPreviousData: true }
  );

  const quotes = data || [];
  const filtered = useMemo(() => {
    if (!query.trim()) return quotes;
    const q = query.toLowerCase();
    return quotes.filter(
      (r) =>
        r.business_name?.toLowerCase().includes(q) ||
        r.contact_name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q)
    );
  }, [quotes, query]);

  const counts = useMemo(() => {
    const c = { all: quotes.length };
    quotes.forEach((q) => { c[q.status] = (c[q.status] || 0) + 1; });
    return c;
  }, [quotes]);

  const onChangeStatus = async (id, next) => {
    try {
      await updateQuoteStatus(id, next);
      mutate();
      toast.success('Status updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this quote permanently?')) return;
    try {
      await deleteQuote(id);
      mutate();
      toast.success('Quote deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid={ADMIN_QUOTES.panel}>
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">Quote requests</div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">Cart submissions</h2>
        </div>
        <button
          type="button"
          onClick={() => window.open(exportQuotesCsvUrl(), '_blank')}
          data-testid={ADMIN_QUOTES.export}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => {
          const active = status === s.slug;
          return (
            <button
              key={s.slug}
              data-testid={ADMIN_QUOTES.filter(s.slug)}
              onClick={() => setStatus(s.slug)}
              className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border transition-colors ${
                active
                  ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)] text-white'
                  : 'border-white/15 text-white/70 hover:border-white hover:text-white'
              }`}
            >
              {s.label}
              <span className="ml-2 text-white/55">{counts[s.slug] ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div className="relative mb-8 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          placeholder="Search business, contact or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none pl-9 pr-4 py-2.5 text-sm text-white transition-colors"
        />
      </div>

      {isLoading && quotes.length === 0 ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="animate-spin text-white/40" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-white/10 py-20 text-center text-white/50 text-sm uppercase tracking-[0.24em]">
          No quotes yet
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((q) => (
            <QuoteCard
              key={q.id}
              quote={q}
              onChangeStatus={onChangeStatus}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function QuoteCard({ quote: q, onChangeStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const colorClass = STATUS_COLOR[q.status] || STATUS_COLOR.closed;
  return (
    <article
      data-testid={ADMIN_QUOTES.row(q.id)}
      className="border border-white/10 bg-[var(--mw-surface)] hover:border-white/25 transition-colors"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-white/55">
              <Building2 size={14} />
              <span className="truncate">{q.business_name}</span>
              <span className="text-white/25">·</span>
              <span className="text-white/45">{(q.items || []).length} items</span>
            </div>
            <h3 className="font-display text-2xl mt-1">{q.contact_name}</h3>
          </div>
          <select
            value={q.status}
            onChange={(e) => onChangeStatus(q.id, e.target.value)}
            data-testid={ADMIN_QUOTES.status(q.id)}
            className={`text-[10px] uppercase tracking-[0.22em] bg-[var(--mw-bg)] px-2.5 py-1.5 border outline-none ${colorClass}`}
          >
            {['new','contacted','quoted','won','lost','closed'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm text-white/70">
          <a href={`mailto:${q.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Mail size={14} className="text-[var(--mw-primary)]" />
            <span className="truncate">{q.email}</span>
          </a>
          <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
            <Phone size={14} className="text-[var(--mw-primary)]" /> {q.phone}
          </a>
        </div>

        {q.notes && (
          <p className="mt-3 text-sm text-white/75 leading-relaxed border-l border-[var(--mw-primary)]/60 pl-4 whitespace-pre-wrap">
            {q.notes}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={ADMIN_QUOTES.toggle(q.id)}
            className="text-[10px] uppercase tracking-[0.22em] text-white/70 hover:text-white flex items-center gap-1.5 transition-colors"
          >
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {open ? 'Hide' : 'Show'} items
          </button>
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">
            {new Date(q.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-[var(--mw-bg)]/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.22em] text-white/45 border-b border-white/10">
                <th className="text-left px-5 sm:px-6 py-2 w-8">#</th>
                <th className="text-left px-2 py-2">Product</th>
                <th className="text-left px-2 py-2">Category</th>
                <th className="text-right px-5 sm:px-6 py-2">Qty</th>
              </tr>
            </thead>
            <tbody>
              {(q.items || []).map((item, i) => (
                <tr key={i} className="border-b border-white/5 last:border-b-0">
                  <td className="px-5 sm:px-6 py-2 text-white/40 tabular-nums">{String(i + 1).padStart(2, '0')}</td>
                  <td className="px-2 py-2 text-white">{item.product_name}</td>
                  <td className="px-2 py-2 text-white/55 uppercase tracking-[0.18em] text-[10px]">{item.category}</td>
                  <td className="px-5 sm:px-6 py-2 text-right text-white tabular-nums">
                    {Number(item.quantity)} {item.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 sm:px-6 py-3 flex justify-end">
            <button
              type="button"
              onClick={() => onDelete(q.id)}
              data-testid={ADMIN_QUOTES.del(q.id)}
              className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[var(--mw-primary)] flex items-center gap-1.5 transition-colors"
            >
              <Trash2 size={12} /> Delete quote
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
